import { Zap, Clock, User, Info } from 'lucide-react';
import { Theme } from '@/shared/constants/themes';

interface JpEvent {
    id: string;
    nickname: string;
    uniqueId: string;
    userId: string;
    item: string;
    comment: string;
    timestamp: number;
    timeAgo: string;
}

interface LiveJpProps {
    jpEvents?: JpEvent[];
    theme?: Theme;
}

export const LiveJp: React.FC<LiveJpProps> = ({ jpEvents = [], theme }) => {
    const themeColors = theme?.colors;
    const recentEvents = jpEvents.slice(0, 5);

    return (
        <div 
            className="rounded-lg shadow-sm border overflow-hidden"
            style={{
                background: `linear-gradient(to bottom right, ${themeColors?.warningLight || '#FEF3C7'}15, ${themeColors?.warning || '#F59E0B'}10)`,
                borderColor: themeColors?.warning || '#F59E0B'
            }}
        >
            {/* Header */}
            <div 
                className="px-4 py-3 border-b backdrop-blur-sm"
                style={{
                    backgroundColor: `${themeColors?.surface || '#FFFFFF'}99`,
                    borderBottomColor: themeColors?.warning || '#F59E0B'
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Zap 
                            size={16} 
                            style={{ color: themeColors?.warning || '#F59E0B' }}
                            fill="currentColor" 
                        />
                        <h3 
                            className="text-sm font-semibold"
                            style={{ color: themeColors?.foreground || '#374151' }}
                        >
                            JP en cours
                        </h3>
                        <div 
                            className="cursor-help transition-colors"
                            style={{ color: themeColors?.warning || '#F59E0B' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = themeColors?.warningDark || '#D97706';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = themeColors?.warning || '#F59E0B';
                            }}
                            title="Déclenché par : JP, je prends, je veux, prix, combien, dispo, ok, go..."
                        >
                            <Info size={14} />
                        </div>
                    </div>
                    <span 
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{
                            color: themeColors?.warningDark || '#92400E',
                            backgroundColor: themeColors?.surface || '#FFFFFF'
                        }}
                    >
                        {recentEvents.length}
                    </span>
                </div>
            </div>

            {/* JP Events List */}
            <div className="p-3 space-y-2">
                {recentEvents.length === 0 ? (
                    <p 
                        className="text-xs italic text-center py-6"
                        style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                    >
                        Aucun JP détecté depuis 5 min
                    </p>
                ) : (
                    recentEvents.map((event) => (
                        <div
                            key={event.id}
                            className="rounded-lg p-2.5 border transition-all backdrop-blur-sm"
                            style={{
                                backgroundColor: `${themeColors?.surface || '#FFFFFF'}E6`,
                                borderColor: themeColors?.warning || '#F59E0B'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.warningDark || '#D97706';
                                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.warning || '#F59E0B';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {/* Item */}
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                <span 
                                    className="text-sm font-bold leading-tight flex-1"
                                    style={{ color: themeColors?.foreground || '#111827' }}
                                >
                                    {event.item || event.comment}
                                </span>
                                <span 
                                    className="text-[10px] font-semibold flex items-center gap-1 whitespace-nowrap"
                                    style={{ color: themeColors?.warningDark || '#92400E' }}
                                >
                                    <Clock size={10} /> {event.timeAgo}
                                </span>
                            </div>

                            {/* User Info */}
                            <div className="flex items-center justify-between gap-2">
                                <div 
                                    className="flex items-center gap-1.5 text-xs min-w-0"
                                    style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                                >
                                    <User size={11} className="flex-shrink-0" />
                                    <span className="font-medium truncate">{event.nickname}</span>
                                </div>
                                <span 
                                    className="text-[10px] font-mono"
                                    style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                                >
                                    @{event.uniqueId}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
